import { Canonical } from '../hashState';
import { Rng, RngState } from '../Rng';
import { advanceTimer } from '../SimClock';
import { TerrainGrid, CellState } from './TerrainGrid';
import { HouseManager, CarRequest } from './HouseManager';

type CarMission = 'none' | 'picking_up' | 'transporting';

export interface Car {
  id: number;
  gridX: number;
  gridY: number;
  targetGridX: number;
  targetGridY: number;
  progress: number; // 0-1 interpolation between current and target
  color: string;
  lifespan: number; // Total time this car will exist
  age: number; // Current age
  opacity: number; // For fade in/out
  state: 'spawning' | 'driving' | 'parked' | 'despawning';
  parkTimer: number; // Time spent parked
  parkDuration: number; // How long to park before moving again
  // Directional movement
  dirX: number; // Current direction X (-1, 0, or 1)
  dirY: number; // Current direction Y (-1, 0, or 1)
  cellsRemaining: number; // How many cells left to travel in current direction
  // Mission/passenger tracking
  mission: CarMission;
  passengerFamilyId: number | null;
  missionPickupHouseId: number | null;
  missionDestinationHouseId: number | null;
  /**
   * The facility gate this leg starts at, when it does not start at a building.
   *
   * A leg names a house *or* a facility, never both, and the house field carries
   * every leg that ever existed before ADR-016. That asymmetry is deliberate: a
   * tagged destination would have been tidier and would have moved the shape of
   * every car in every golden master in this repository. See `legTarget`.
   */
  missionPickupFacilityId: number | null;
  /** The gate this leg ends at: somebody going on shift. */
  missionDestinationFacilityId: number | null;
  missionTimer: number; // Time spent on current mission
  // Navigation history to prevent oscillation
  recentPositions: { x: number; y: number }[];
  // Full path history for current mission leg (for route visualization)
  missionPathHistory: { x: number; y: number }[];
  // Parking spot seeking
  seekingParkingSpot: boolean;
  parkingTargetX: number | null;
  parkingTargetY: number | null;
  /**
   * The freight leg this vehicle is running, or `null` — which is every vehicle
   * that belongs to this district.
   *
   * The one field that decides what kind of thing a `Car` is. A truck has no
   * passenger, no home, no parking spot and no lifespan that matters: it enters at
   * an interchange, drives to another, and leaves. Everything else about it — how
   * fast it goes, what it has to drive around, and what it kills on the way — is
   * the behaviour every vehicle on this map has always had, which is the whole
   * reason freight is a truck here rather than a number (`FreightManager`).
   *
   * Nullable rather than a `kind` discriminator so that a district with no
   * corridor hashes to what it hashed to before there was freight: `canonicalState`
   * writes the freight keys only when they are set, which is the idiom the facility
   * ids introduced in ADR-016.
   */
  freight: FreightCargo | null;
}

/**
 * What a truck is carrying, as traffic needs to know it.
 *
 * A leg id and a cell. Not what is in the trailer, not who sold it, not who is
 * paying for the crossing — a cargo here is a destination and a name to report the
 * outcome under, and `CarManager` has never needed more than a cell to drive to.
 */
export interface FreightCargo {
  /** Names this leg in `takeFreightOutcomes`. Assigned by `FreightManager`. */
  legId: number;
  /** The cell it leaves the map at — or delivers to, for a convoy. */
  toX: number;
  toY: number;
  /**
   * The building site this load is for, or `null` for a transit crossing.
   *
   * §7's construction convoys, and the one thing that distinguishes them from the
   * trucks that are only passing through. It is on the *cargo* rather than on the
   * vehicle because it is a fact about what the lorry is carrying and to whom —
   * traffic still knows only a cell to drive to, and drives to it the same way.
   *
   * Nullable rather than a second kind of `Car`, on `freight`'s own terms: a
   * district with no corridor and no building site hashes to what it hashed to
   * before either existed, because `canonicalState` writes the key only when it is
   * set.
   */
  facilityId: number | null;
}

/** How one truck's crossing ended. Drained every tick by `SurfaceSim`. */
export interface FreightOutcome {
  legId: number;
  /** True: it reached the far interchange, or the gate. False: it gave up. */
  arrived: boolean;
  /** The building site it was delivering to, or `null` for a transit crossing. */
  facilityId: number | null;
}

/**
 * Every car, plus the traffic trails they have laid. See `CarManager.save()`.
 *
 * The BFS distance cache is absent on purpose: it is derived from the grid and
 * carries a terrain version to say so, so a restored world rebuilds it on
 * demand and reaches the same answers.
 */
export interface CarSnapshot {
  cars: Car[];
  nextCarId: number;
  spawnTimer: number;
  requestCheckTimer: number;
  trails: Array<{ cell: string; p: [number, number, number, number] }>;
  rng: RngState;
}

export interface CarManagerConfig {
  BASE_MAX_CARS: number;
  PAVED_CELLS_PER_CAR: number;
  SPAWN_INTERVAL: number;
  MOVE_SPEED: number;
  HIGHWAY_SPEED: number;
  MISSION_TIMEOUT: number;
  /**
   * How long a truck keeps trying to cross the district, in sim-ms.
   *
   * Its own number rather than `MISSION_TIMEOUT`, because the two are patience for
   * two different journeys. A commuter's mission is a house to a shop — a few
   * cells — and thirty seconds is generous. A transit crossing is the longest trip
   * anything makes on this map: thirty-one cells at 600 ms each is nineteen
   * sim-seconds *before* a single yield, so at the commuter's patience half of all
   * freight failed on an empty road at perfect road quality, and §5's
   * *"routes × road quality × congestion"* was measuring the constant instead.
   *
   * Three times a commuter's, which is a crossing at walking pace with room to
   * yield. Above it a truck gives up and the leg is abandoned, which is what makes
   * the failure rate a fact about the district's roads rather than about this
   * number.
   */
  FREIGHT_TIMEOUT: number;
  FADE_DURATION: number;
  SPEED_MULTIPLIER: number;
  // Traffic organization
  TRAFFIC_MODE: number;          // 0=off, 1=right-hand lanes, 2=pheromone trails
  LANE_BIAS_STRENGTH: number;    // Scoring bias for right-hand lanes (mode 1)
  PHEROMONE_DEPOSIT: number;     // Pheromone deposited per car per cell
  PHEROMONE_DECAY: number;       // Fraction of pheromone that decays per second
  PHEROMONE_INFLUENCE: number;   // Scoring influence of pheromone trails (mode 2)
}

export const DEFAULT_CAR_CONFIG: CarManagerConfig = {
  BASE_MAX_CARS: 1,
  PAVED_CELLS_PER_CAR: 10,
  SPAWN_INTERVAL: 800,
  MOVE_SPEED: 600,
  HIGHWAY_SPEED: 300,
  MISSION_TIMEOUT: 30000,
  FREIGHT_TIMEOUT: 90000,
  FADE_DURATION: 400,
  SPEED_MULTIPLIER: 1.0,
  TRAFFIC_MODE: 1,
  LANE_BIAS_STRENGTH: 1.5,
  PHEROMONE_DEPOSIT: 1.0,
  PHEROMONE_DECAY: 0.15,
  PHEROMONE_INFLUENCE: 2.0,
};

/**
 * Where a facility's staff are dropped off and picked up.
 *
 * One question, asked of `FacilityManager` — which is where a gate is defined and
 * why. Injected as an interface rather than as the manager so that traffic keeps
 * knowing nothing about slabs, uptime, or who owns the ground: a gate is a cell,
 * and a cell is all a car has ever needed.
 */
export interface FacilityGates {
  gateOf(facilityId: number): { x: number; y: number } | null;
}

/**
 * A car as a tooltip wants it: what it is doing and who for.
 *
 * The facility ids are `null` for every trip between two buildings, which is
 * every trip in a district with nothing federal on it.
 */
export interface CarInfo {
  state: string;
  mission: string;
  passengerFamilyId: number | null;
  missionPickupHouseId: number | null;
  missionDestinationHouseId: number | null;
  missionPickupFacilityId: number | null;
  missionDestinationFacilityId: number | null;
}

function describeCar(car: Car): CarInfo {
  return {
    state: car.state,
    mission: car.mission,
    passengerFamilyId: car.passengerFamilyId,
    missionPickupHouseId: car.missionPickupHouseId,
    missionDestinationHouseId: car.missionDestinationHouseId,
    missionPickupFacilityId: car.missionPickupFacilityId,
    missionDestinationFacilityId: car.missionDestinationFacilityId,
  };
}

export class CarManager {
  private cars: Car[] = [];
  private grid: TerrainGrid;
  private rng: Rng;
  private houseManager: HouseManager | null = null;
  private facilityGates: FacilityGates | null = null;
  private nextCarId = 0;
  private spawnTimer = 0;
  private requestCheckTimer = 0;
  private config: CarManagerConfig;

  /**
   * How the trucks that finished this tick finished.
   *
   * An out-tray rather than a callback, for the reason every wire between two
   * managers in this package is one: traffic cannot see a corridor, a toll or a
   * ledger, and a `FreightManager` handed to it would be all three. `SurfaceSim`
   * drains it immediately after `update`, so it is empty at every snapshot
   * boundary and is not part of the saved state.
   */
  private freightOutcomes: FreightOutcome[] = [];
  private readonly REQUEST_CHECK_INTERVAL = 500; // ms between checking car requests

  // BFS distance cache for navigation
  private bfsCache: Map<string, { distMap: Map<string, number>; terrainVersion: number }> = new Map();

  // Directional pheromone trails: "x,y" → pheromone level per direction
  private pheromones: Map<string, [number, number, number, number]> = new Map(); // [right, down, left, up]

  // Car colors - cheerful, corporate-friendly
  private static readonly COLORS = [
    '#4a90a4', // Teal
    '#a4524a', // Burgundy
    '#5a5a5a', // Gray
    '#f0f0f0', // White
    '#2d2d2d', // Black
    '#7a8b5a', // Olive
    '#8b7355', // Tan
    '#4a6fa4', // Blue
    '#c4a44a', // Gold
    '#8a4a6a', // Mauve
  ];

  constructor(grid: TerrainGrid, rng: Rng, config: Partial<CarManagerConfig> = {}) {
    this.grid = grid;
    this.rng = rng;
    this.config = { ...DEFAULT_CAR_CONFIG, ...config };
  }

  getConfig(): CarManagerConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<CarManagerConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /**
   * Set the HouseManager for car dispatch integration.
   */
  setHouseManager(houseManager: HouseManager): void {
    this.houseManager = houseManager;
  }

  /**
   * Set the source of facility gates, for commuting.
   *
   * Optional in the sense that a `CarManager` with none simply never resolves a
   * facility leg, and a district with no facility on it never asks for one.
   */
  setFacilityGates(gates: FacilityGates): void {
    this.facilityGates = gates;
  }

  /**
   * A vehicle with nothing decided about it, at a cell.
   *
   * Draws nothing. `trySpawnCar` still rolls its own colour, lifespan, heading and
   * run length inline rather than through here, and that is not an oversight: those
   * four draws are in every golden master in this repository in that order, and a
   * refactor that moved them would re-record the corpus to prove a truck exists.
   *
   * A truck's four are constants instead. It has no colour to pick (the renderer
   * knows what a truck looks like), no lifespan that matters (it leaves when its
   * leg ends), and no initial heading worth drawing, because it is navigating from
   * its first tick.
   */
  private blankCar(x: number, y: number): Car {
    return {
      id: this.nextCarId++,
      gridX: x,
      gridY: y,
      targetGridX: x,
      targetGridY: y,
      progress: 0,
      color: CarManager.COLORS[0],
      lifespan: this.config.FREIGHT_TIMEOUT,
      age: 0,
      opacity: 0,
      state: 'spawning',
      parkTimer: 0,
      parkDuration: 0,
      dirX: 1,
      dirY: 0,
      cellsRemaining: 0,
      mission: 'none',
      passengerFamilyId: null,
      missionPickupHouseId: null,
      missionDestinationHouseId: null,
      missionPickupFacilityId: null,
      missionDestinationFacilityId: null,
      missionTimer: 0,
      recentPositions: [],
      missionPathHistory: [{ x, y }],
      seekingParkingSpot: false,
      parkingTargetX: null,
      parkingTargetY: null,
      freight: null,
    };
  }

  /**
   * Put a truck on the map, running one freight leg.
   *
   * Called by `SurfaceSim` on a settled budget cycle. Returns false when there is
   * no room at the gate — a car is parked on both cells of the interchange, which
   * is a corridor blocked by the district's own traffic and is counted as a lapsed
   * leg rather than as a truck that failed.
   *
   * The truck spawns *on* the interchange, because the interchange is road. It is
   * the only vehicle in this game that appears somewhere specific: every other car
   * spawns wherever there is space, having always been here.
   */
  dispatchTruck(
    legId: number,
    from: { x: number; y: number },
    to: { x: number; y: number },
    facilityId: number | null = null
  ): boolean {
    const spot = this.freeGateCell(from);
    if (!spot) return false;

    const truck: Car = {
      ...this.blankCar(spot.x, spot.y),
      // Not a passenger colour. Trucks are drawn as their own thing by the
      // renderer; this is what a client that ignored the distinction would get.
      color: '#6b6257',
      mission: 'transporting',
      freight: { legId, toX: to.x, toY: to.y, facilityId },
    };
    this.cars.push(truck);
    return true;
  }

  /**
   * Whichever cell of an interchange nothing is standing on.
   *
   * The named cell first, then its neighbour to the east and to the south — the
   * two an interchange can occupy, tried in a fixed order because which one a truck
   * appears on is a simulation fact.
   */
  private freeGateCell(from: { x: number; y: number }): { x: number; y: number } | null {
    const candidates = [
      { x: from.x, y: from.y },
      { x: from.x + 1, y: from.y },
      { x: from.x, y: from.y + 1 },
    ];
    for (const spot of candidates) {
      const wrapped = { x: this.wrapX(spot.x), y: this.wrapY(spot.y) };
      if (!this.canMoveToTerrain(wrapped.x, wrapped.y)) continue;
      const taken = this.cars.some(
        (c) =>
          c.state !== 'despawning' &&
          ((this.wrapX(c.gridX) === wrapped.x && this.wrapY(c.gridY) === wrapped.y) ||
            (this.wrapX(c.targetGridX) === wrapped.x && this.wrapY(c.targetGridY) === wrapped.y))
      );
      if (!taken) return wrapped;
    }
    return null;
  }

  /** Every truck outcome since the last drain. Empties the tray. */
  takeFreightOutcomes(): FreightOutcome[] {
    if (this.freightOutcomes.length === 0) return [];
    const out = this.freightOutcomes;
    this.freightOutcomes = [];
    return out;
  }

  /**
   * Take a truck off the map and report how its crossing ended.
   *
   * Called wherever a truck's mission would otherwise be cleared: it arrived, it
   * timed out, or its destination stopped existing. A truck whose mission was
   * cleared without this would become an ownerless vehicle wandering the district
   * forever, and its leg would never be counted at either end.
   */
  private endFreight(car: Car, arrived: boolean): void {
    if (!car.freight) return;
    this.freightOutcomes.push({
      legId: car.freight.legId,
      arrived,
      facilityId: car.freight.facilityId,
    });
    this.clearMission(car);
    car.state = 'despawning';
    // The fade is driven by `age - lifespan`, which is how a car that has outlived
    // its lifespan leaves. A truck leaves because its *leg* ended, at whatever age
    // that happened — so its lifespan is now. Without this line a truck that
    // arrived early would sit on the road it delivered to, at full opacity, until
    // its notional lifespan ran out: blocking the gate against the next leg,
    // counted in the traffic, and still on the map.
    car.lifespan = car.age;
    car.opacity = 1;
  }

  /**
   * Get current max cars based on paved area and highway availability.
   */
  private getMaxCars(): number {
    const pavedCells = this.grid.countPavedCells();
    const pavedBonus = this.config.PAVED_CELLS_PER_CAR > 0
      ? Math.floor(pavedCells / this.config.PAVED_CELLS_PER_CAR)
      : 0;

    return this.config.BASE_MAX_CARS + pavedBonus;
  }

  /** How long this vehicle keeps trying. See `FREIGHT_TIMEOUT`. */
  private patienceFor(car: Car): number {
    return car.freight ? this.config.FREIGHT_TIMEOUT : this.config.MISSION_TIMEOUT;
  }

  /** Vehicles that belong to this district. Trucks are somebody else's. */
  private residentCount(): number {
    let n = 0;
    for (const car of this.cars) if (car.freight === null) n++;
    return n;
  }

  /**
   * Get move speed for a car at a specific position.
   * Lower SPEED_MULTIPLIER = slower cars (higher ms per cell).
   */
  private getMoveSpeed(gridX: number, gridY: number): number {
    const baseSpeed = this.grid.isHighwayCell(gridX, gridY)
      ? this.config.HIGHWAY_SPEED
      : this.config.MOVE_SPEED;
    // SPEED_MULTIPLIER < 1 means slower (divide to increase ms-per-cell)
    return this.config.SPEED_MULTIPLIER > 0
      ? baseSpeed / this.config.SPEED_MULTIPLIER
      : baseSpeed;
  }

  /**
   * Get right-hand lane bias for a candidate cell given a travel direction.
   * Returns a score penalty (positive = wrong side, negative = correct side).
   *
   * Right-hand traffic rules:
   *   Horizontal highway: going right → stay on bottom, going left → stay on top
   *   Vertical highway: going down → stay on left, going up → stay on right
   */
  private getLaneBias(cellX: number, cellY: number, dirX: number, dirY: number): number {
    if (this.config.TRAFFIC_MODE !== 1) return 0;

    const info = this.grid.getHighwayLaneInfo(cellX, cellY);
    if (!info) return 0;

    // offset: -1 = top/left edge, +1 = bottom/right edge
    const { orientation, offset } = info;

    // Desired offset sign for right-hand traffic:
    // Horizontal: going right (dx=1) → want bottom (+offset), going left (dx=-1) → want top (-offset)
    // Vertical: going down (dy=1) → want left (-offset), going up (dy=-1) → want right (+offset)
    let desiredSign: number;
    if (orientation === 'horizontal') {
      desiredSign = dirX; // +1 for right (want +offset), -1 for left (want -offset)
    } else {
      desiredSign = -dirY; // +1 for up (want +offset/right), -1 for down (want -offset/left)
    }

    // If direction is perpendicular to highway (e.g. crossing), no bias
    if (desiredSign === 0) return 0;

    // Alignment: +1 = perfectly on correct side, -1 = wrong side, 0 = center
    const alignment = offset * desiredSign;

    // Return bias: reward correct side, penalize wrong side
    return -alignment * this.config.LANE_BIAS_STRENGTH;
  }

  /**
   * Get pheromone-based traffic bias for a candidate cell given a travel direction.
   * Cars prefer cells with same-direction pheromone and avoid opposite-direction pheromone.
   */
  private getPheromeBias(cellX: number, cellY: number, dirX: number, dirY: number): number {
    if (this.config.TRAFFIC_MODE !== 2) return 0;

    // Only apply on highways
    if (!this.grid.isHighwayCell(cellX, cellY)) return 0;

    const key = `${cellX},${cellY}`;
    const p = this.pheromones.get(key);
    if (!p) return 0;

    // Direction indices: right=0, down=1, left=2, up=3
    const dirIndex = this.dirToIndex(dirX, dirY);
    if (dirIndex < 0) return 0;

    const oppositeIndex = (dirIndex + 2) % 4;

    const sameDir = p[dirIndex];
    const oppositeDir = p[oppositeIndex];

    // Bonus for same-direction pheromone, penalty for opposite
    // Use logarithmic scaling so high pheromone doesn't dominate infinitely
    const influence = this.config.PHEROMONE_INFLUENCE;
    const sameBonus = -Math.log1p(sameDir) * influence * 0.5;     // attract to same-direction trails
    const oppositePenalty = Math.log1p(oppositeDir) * influence;   // repel from oncoming trails

    return sameBonus + oppositePenalty;
  }

  /** Convert direction to pheromone array index. */
  private dirToIndex(dx: number, dy: number): number {
    if (dx === 1 && dy === 0) return 0;  // right
    if (dx === 0 && dy === 1) return 1;  // down
    if (dx === -1 && dy === 0) return 2; // left
    if (dx === 0 && dy === -1) return 3; // up
    return -1;
  }

  /** Deposit pheromone at a cell in the car's travel direction. */
  private depositPheromone(x: number, y: number, dirX: number, dirY: number): void {
    if (this.config.TRAFFIC_MODE !== 2) return;
    if (!this.grid.isHighwayCell(x, y)) return;

    const dirIdx = this.dirToIndex(dirX, dirY);
    if (dirIdx < 0) return;

    const key = `${x},${y}`;
    let p = this.pheromones.get(key);
    if (!p) {
      p = [0, 0, 0, 0];
      this.pheromones.set(key, p);
    }
    p[dirIdx] += this.config.PHEROMONE_DEPOSIT;
  }

  /** Decay all pheromone values. Called once per update tick. */
  private decayPheromones(dt: number): void {
    if (this.config.TRAFFIC_MODE !== 2) {
      // Clear pheromones when not in pheromone mode
      if (this.pheromones.size > 0) this.pheromones.clear();
      return;
    }

    const decay = Math.pow(1 - this.config.PHEROMONE_DECAY, dt / 1000);
    const threshold = 0.01;

    for (const [key, p] of this.pheromones) {
      p[0] *= decay;
      p[1] *= decay;
      p[2] *= decay;
      p[3] *= decay;

      // Remove entries that are effectively zero
      if (p[0] < threshold && p[1] < threshold && p[2] < threshold && p[3] < threshold) {
        this.pheromones.delete(key);
      }
    }
  }

  /**
   * Process pending car requests from HouseManager.
   */
  private processCarRequests(): void {
    if (!this.houseManager) return;

    const requests = this.houseManager.getPendingCarRequests();
    if (requests.length === 0) return;

    // Find available (parked with no mission) cars. A truck is never one: it is
    // always on a leg, and a district cannot dispatch somebody else's freight to
    // fetch a family from the shops.
    const availableCars = this.cars.filter(c =>
      c.state === 'parked' &&
      c.mission === 'none' &&
      c.passengerFamilyId === null &&
      c.freight === null
    );

    for (const request of requests) {
      if (availableCars.length === 0) break;

      // Either a building or a facility gate — a family clocking off is standing
      // on federal concrete and the car still has to fetch them.
      const pickup = this.targetPosition(
        request.pickupHouseId,
        request.pickupFacilityId ?? null
      );
      if (!pickup) continue;

      // Find the nearest available car to the pickup location
      let nearestCar: Car | null = null;
      let nearestDistance = Infinity;

      for (const car of availableCars) {
        const dist = this.wrappedDistance(car.gridX, car.gridY, pickup.x, pickup.y);
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearestCar = car;
        }
      }

      if (nearestCar) {
        // Dispatch this car
        nearestCar.mission = 'picking_up';
        nearestCar.missionPickupHouseId = request.pickupHouseId;
        nearestCar.missionDestinationHouseId = request.destinationHouseId;
        nearestCar.missionPickupFacilityId = request.pickupFacilityId ?? null;
        nearestCar.missionDestinationFacilityId = request.destinationFacilityId ?? null;
        nearestCar.passengerFamilyId = request.familyId;
        nearestCar.missionTimer = 0; // Start mission timer
        nearestCar.missionPathHistory = [{ x: nearestCar.gridX, y: nearestCar.gridY }];
        // Clear parking seeking state
        nearestCar.seekingParkingSpot = false;
        nearestCar.parkingTargetX = null;
        nearestCar.parkingTargetY = null;

        // Start driving towards pickup
        this.navigateToLeg(nearestCar);

        // Remove from available pool
        const idx = availableCars.indexOf(nearestCar);
        if (idx >= 0) availableCars.splice(idx, 1);

        // Clear the request
        this.houseManager.clearCarRequest(request.familyId);
      }
    }
  }

  /**
   * Where one end of a trip is, whichever kind of end it is.
   *
   * A house's origin cell, or a facility's gate — the middle of the south edge of
   * its slab (`facilityGate`). The two are interchangeable from here on: a car
   * arrives at either by reaching a paved cell within two of it, which is the rule
   * traffic has navigated by since long before there was anything federal to
   * drive to.
   */
  private targetPosition(
    houseId: number | null,
    facilityId: number | null
  ): { x: number; y: number } | null {
    if (facilityId !== null) return this.facilityGates?.gateOf(facilityId) ?? null;
    if (houseId === null || !this.houseManager) return null;
    const house = this.houseManager.getHouseById(houseId);
    return house ? { x: house.gridX, y: house.gridY } : null;
  }

  /**
   * Where this car is headed right now: its pickup, or its drop-off.
   *
   * A truck's is a cell rather than an entity, and that is the third kind of end a
   * trip can have. The other two are a building and a facility gate, both of which
   * are things that could move or stop existing; an interchange cell is neither, so
   * the destination travels with the cargo instead of being looked up.
   */
  private legTarget(car: Car): { x: number; y: number } | null {
    if (car.freight) return { x: car.freight.toX, y: car.freight.toY };
    return car.mission === 'picking_up'
      ? this.targetPosition(car.missionPickupHouseId, car.missionPickupFacilityId)
      : this.targetPosition(car.missionDestinationHouseId, car.missionDestinationFacilityId);
  }

  /**
   * Navigate a car towards the current leg of its mission, using BFS-guided
   * navigation.
   *
   * Uses a BFS distance map as primary scoring (replaces Manhattan distance),
   * with anti-oscillation penalty and momentum bonus as secondary tiebreakers.
   * Car-blocked cells get a heavy penalty but aren't excluded to prevent deadlocks.
   *
   * A facility gate is a *sealed* cell, so the BFS seeds from the paved cells
   * within two of it and the car stops on the road outside. A facility with no
   * road within two cells of its gate is one nobody can reach: the mission times
   * out, the family goes back to waiting, and the posts stay filled by people who
   * cannot get to work. That is a consequence of the map, and the map is the
   * mayor's.
   */
  private navigateToLeg(car: Car): void {
    // A truck needs no house list: its destination came with its cargo.
    if (!this.houseManager && !car.freight) return;

    const target = this.legTarget(car);
    if (!target) {
      if (car.freight) this.endFreight(car, false);
      else this.clearMission(car);
      return;
    }

    const houseX = target.x;
    const houseY = target.y;

    // Check if already at destination
    const currentDist = this.wrappedDistance(car.gridX, car.gridY, houseX, houseY);
    if (currentDist <= 2) {
      this.handleArrival(car);
      return;
    }

    // Get BFS distance map for this house
    const bfsMap = this.getBFSDistanceMap(houseX, houseY);

    // Check if car's current position is reachable at all
    const carKey = `${this.wrapX(car.gridX)},${this.wrapY(car.gridY)}`;
    if (!bfsMap.has(carKey)) {
      // Car is not on a paved cell connected to the house - park and wait for terrain change
      car.state = 'parked';
      car.parkTimer = 0;
      car.parkDuration = this.rng.range(500, 1500);
      return;
    }

    const directions = [
      { dx: 1, dy: 0 },   // right
      { dx: 0, dy: 1 },   // down
      { dx: -1, dy: 0 },  // left
      { dx: 0, dy: -1 },  // up
    ];

    // Score each direction (lower is better)
    let bestDir: { dx: number; dy: number } | null = null;
    let bestScore = Infinity;
    let allTerrainBlocked = true;
    let allCarBlocked = true;

    for (const dir of directions) {
      const newX = this.wrapX(car.gridX + dir.dx);
      const newY = this.wrapY(car.gridY + dir.dy);

      // Check terrain only (ignore cars)
      if (!this.canMoveToTerrain(car.gridX + dir.dx, car.gridY + dir.dy)) {
        continue;
      }
      allTerrainBlocked = false;

      // Base score: BFS distance (exact shortest path distance)
      const bfsKey = `${newX},${newY}`;
      let score: number;
      if (bfsMap.has(bfsKey)) {
        score = bfsMap.get(bfsKey)!;
      } else {
        // Cell is paved but not connected to the house - very high score
        score = 9999;
      }

      // Heavy penalty for car-occupied cells (but don't exclude them)
      const carOccupied = this.isCarOccupied(car, newX, newY);
      if (carOccupied) {
        score += 100;
      } else {
        allCarBlocked = false;
      }

      // Penalty for recently visited positions (prevents oscillation)
      for (let i = 0; i < car.recentPositions.length; i++) {
        const pos = car.recentPositions[car.recentPositions.length - 1 - i];
        if (pos.x === newX && pos.y === newY) {
          score += 8 - i * 2;
          break;
        }
      }

      // Momentum bonus: favor continuing in same direction
      if (car.dirX === dir.dx && car.dirY === dir.dy) {
        score -= 0.5;
      }

      // Small penalty for turning
      if ((car.dirX !== 0 || car.dirY !== 0) && (car.dirX !== dir.dx || car.dirY !== dir.dy)) {
        score += 0.1;
      }

      // Traffic organization on highways
      score += this.getLaneBias(newX, newY, dir.dx, dir.dy);
      score += this.getPheromeBias(newX, newY, dir.dx, dir.dy);

      if (score < bestScore) {
        bestScore = score;
        bestDir = dir;
      }
    }

    if (allTerrainBlocked || !bestDir) {
      // No paved cells adjacent - park and wait
      car.state = 'parked';
      car.parkTimer = 0;
      car.parkDuration = this.rng.range(200, 500);
      return;
    }

    if (allCarBlocked) {
      // All paved directions are car-blocked - park briefly to yield
      car.state = 'parked';
      car.parkTimer = 0;
      car.parkDuration = this.rng.range(150, 400);
      return;
    }

    car.dirX = bestDir.dx;
    car.dirY = bestDir.dy;
    car.cellsRemaining = 1; // Move one cell at a time for mission nav
    car.targetGridX = this.wrapX(car.gridX + bestDir.dx);
    car.targetGridY = this.wrapY(car.gridY + bestDir.dy);
    car.progress = 0;
    car.state = 'driving';
  }

  /**
   * Calculate Manhattan distance with wraparound consideration.
   */
  private wrappedDistance(x1: number, y1: number, x2: number, y2: number): number {
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();

    // Direct distance
    const dx = Math.abs(x2 - x1);
    const dy = Math.abs(y2 - y1);

    // Wrapped distance
    const wdx = Math.min(dx, cols - dx);
    const wdy = Math.min(dy, rows - dy);

    return wdx + wdy;
  }

  /**
   * Compute a BFS distance map from paved cells near a target position.
   * BFS expands over all paved cells, giving exact shortest-path distances.
   */
  private computeBFSDistanceMap(targetX: number, targetY: number): Map<string, number> {
    const distMap = new Map<string, number>();
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();
    const queue: { x: number; y: number }[] = [];

    // Seed BFS from paved cells within distance 2 of target
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const cx = this.wrapX(targetX + dx);
        const cy = this.wrapY(targetY + dy);
        const cell = this.grid.getCell(cx, cy);
        if (cell && this.isPaved(cell.state)) {
          const key = `${cx},${cy}`;
          if (!distMap.has(key)) {
            distMap.set(key, 0);
            queue.push({ x: cx, y: cy });
          }
        }
      }
    }

    // BFS expansion over paved cells
    const directions = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    let head = 0;
    while (head < queue.length) {
      const cur = queue[head++];
      const curDist = distMap.get(`${cur.x},${cur.y}`)!;

      for (const dir of directions) {
        const nx = this.wrapX(cur.x + dir.dx);
        const ny = this.wrapY(cur.y + dir.dy);
        const key = `${nx},${ny}`;

        if (distMap.has(key)) continue;

        const cell = this.grid.getCell(nx, ny);
        if (cell && this.isPaved(cell.state)) {
          distMap.set(key, curDist + 1);
          queue.push({ x: nx, y: ny });
        }
      }
    }

    return distMap;
  }

  /**
   * Get a cached BFS distance map for a house position. Invalidates on terrain change.
   */
  private getBFSDistanceMap(houseX: number, houseY: number): Map<string, number> {
    const key = `${houseX},${houseY}`;
    const terrainVersion = this.grid.getTerrainVersion();
    const cached = this.bfsCache.get(key);

    if (cached && cached.terrainVersion === terrainVersion) {
      return cached.distMap;
    }

    // Evict old entries if cache is too large
    if (this.bfsCache.size > 50) {
      this.bfsCache.clear();
    }

    const distMap = this.computeBFSDistanceMap(houseX, houseY);
    this.bfsCache.set(key, { distMap, terrainVersion });
    return distMap;
  }

  /**
   * Check if a cell is paved (ignoring car occupancy).
   */
  private canMoveToTerrain(newX: number, newY: number): boolean {
    const wrappedX = this.wrapX(newX);
    const wrappedY = this.wrapY(newY);
    const cell = this.grid.getCell(wrappedX, wrappedY);
    return cell !== null && this.isPaved(cell.state);
  }

  /**
   * Check if another car occupies a cell (excluding a specific car).
   */
  private isCarOccupied(excludeCar: Car, wrappedX: number, wrappedY: number): boolean {
    return this.cars.some(c =>
      c !== excludeCar &&
      c.state !== 'despawning' &&
      ((this.wrapX(c.gridX) === wrappedX && this.wrapY(c.gridY) === wrappedY) ||
       (this.wrapX(c.targetGridX) === wrappedX && this.wrapY(c.targetGridY) === wrappedY))
    );
  }

  /**
   * Check if car has arrived at its mission destination.
   */
  private checkMissionProgress(car: Car): void {
    if ((!this.houseManager && !car.freight) || car.mission === 'none') return;

    const target = this.legTarget(car);
    if (!target) return;

    // Check if car is adjacent to the target (using wrapped distance for toroidal grid)
    const dist = this.wrappedDistance(car.gridX, car.gridY, target.x, target.y);
    if (dist <= 2) {
      this.handleArrival(car);
    }
  }

  /**
   * Handle car arriving at a mission destination.
   *
   * The drop-off is the one place the two kinds of destination behave
   * differently, and they have to: arriving at a *building* tells the economy a
   * customer walked in and may make the family copy a neighbour's subscription.
   * Arriving at a data centre is a shift starting, and a data centre sells
   * nothing to nobody. See `HouseManager.familyArrivedAtFacility`.
   */
  private handleArrival(car: Car): void {
    // A truck's arrival is the end of the whole trip: it entered the map at one
    // interchange and it leaves at this one. Nobody gets out, nothing is sold, and
    // the district finds out only that a crossing completed.
    if (car.freight) {
      this.endFreight(car, true);
      return;
    }

    if (!this.houseManager) return;

    if (car.mission === 'picking_up' && car.passengerFamilyId !== null) {
      // Picked up family - notify HouseManager
      this.houseManager.familyPickedUp(car.passengerFamilyId);
      car.mission = 'transporting';
      car.missionTimer = 0; // Reset timer for transport leg
      car.missionPathHistory = [{ x: car.gridX, y: car.gridY }]; // Reset for new leg

      // Now drive to destination
      this.navigateToLeg(car);
    } else if (car.mission === 'transporting' && car.passengerFamilyId !== null) {
      // Dropped off family - notify HouseManager
      if (car.missionDestinationFacilityId !== null) {
        this.houseManager.familyArrivedAtFacility(
          car.passengerFamilyId,
          car.missionDestinationFacilityId
        );
      } else if (car.missionDestinationHouseId !== null) {
        this.houseManager.familyDroppedOff(car.passengerFamilyId, car.missionDestinationHouseId);
      }

      // Clear mission
      this.clearMission(car);

      // Park briefly
      car.state = 'parked';
      car.parkTimer = 0;
      car.parkDuration = this.rng.range(1000, 3000);
    }
  }

  /**
   * Handle mission timeout - return family home if transporting, or give up pickup.
   */
  private handleMissionTimeout(car: Car): void {
    // A truck that ran out of time turns back. There is nowhere to take it — its
    // origin is a gate it has already left — so it simply leaves, and the leg is
    // recorded as abandoned. §5's *"routes × road quality × congestion"* is that
    // count against the dispatched one: a corridor with no road across it, or one
    // too congested to finish a crossing in half a minute, carries less freight.
    if (car.freight) {
      this.endFreight(car, false);
      return;
    }

    if (!this.houseManager) return;

    if (car.mission === 'transporting' && car.passengerFamilyId !== null) {
      // Family is in car - take them home instead
      const family = this.houseManager.getFamily(car.passengerFamilyId);
      if (family) {
        car.missionDestinationHouseId = family.homeHouseId;
        // Whatever they set out for, they are going home now. A commuter whose
        // shift never started is a commuter, not an absentee: nothing here docks
        // anybody, because a wage is per job and not per arrival.
        car.missionDestinationFacilityId = null;
        car.missionTimer = 0; // Reset timer for return trip
        this.navigateToLeg(car);
      } else {
        this.clearMission(car);
      }
    } else if (car.mission === 'picking_up') {
      // Failed to pick up - cancel mission
      if (car.passengerFamilyId !== null) {
        // Reset family state back to home
        this.houseManager.familyCancelledPickup(car.passengerFamilyId);
      }
      this.clearMission(car);
    }
  }

  /**
   * Clear all mission data from a car.
   */
  private clearMission(car: Car): void {
    car.mission = 'none';
    car.passengerFamilyId = null;
    car.missionPickupHouseId = null;
    car.missionDestinationHouseId = null;
    car.missionPickupFacilityId = null;
    car.missionDestinationFacilityId = null;
    car.missionTimer = 0;
    car.missionPathHistory = [];
  }

  update(dt: number): void {
    // Spawn new cars periodically (more cars allowed with highways). Timers carry
    // their remainder so traffic density follows sim-time, not tick size.
    const spawn = advanceTimer(this.spawnTimer, dt, this.config.SPAWN_INTERVAL, 1);
    this.spawnTimer = spawn.value;
    const maxCars = this.getMaxCars();
    // Against the district's own vehicles. Freight passing through does not use up
    // a town's traffic budget — the trucks are extra, which is what makes a
    // corridor congestion rather than a substitution.
    if (spawn.fires > 0 && this.residentCount() < maxCars) {
      this.trySpawnCar();
    }

    // Check for pending car requests from families
    const check = advanceTimer(this.requestCheckTimer, dt, this.REQUEST_CHECK_INTERVAL, 1);
    this.requestCheckTimer = check.value;
    if (check.fires > 0 && this.houseManager) {
      this.processCarRequests();
    }

    // Decay pheromone trails
    this.decayPheromones(dt);

    // Update each car
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i];
      car.age += dt;

      switch (car.state) {
        case 'spawning':
          car.opacity = Math.min(1, car.age / this.config.FADE_DURATION);
          if (car.opacity >= 1) {
            if (car.freight) {
              // A truck has somewhere to be. It does not arrive in a district and
              // then wonder what to do, and it draws nothing to decide.
              this.navigateToLeg(car);
            } else {
              car.state = 'parked';
              car.parkTimer = 0;
              car.parkDuration = this.rng.range(1000, 4000);
            }
          }
          break;

        case 'parked':
          car.parkTimer += dt;

          // Track mission time even while parked (waiting for road)
          if (car.mission !== 'none') {
            car.missionTimer += dt;
            if (car.missionTimer >= this.patienceFor(car)) {
              this.handleMissionTimeout(car);
              break;
            }
          }

          if (car.parkTimer >= car.parkDuration) {
            // Check if it's time to despawn (only if no passengers!)
            if (car.age >= car.lifespan && car.mission === 'none') {
              car.state = 'despawning';
              car.opacity = 1;
            } else if (car.mission !== 'none') {
              // On a mission - continue navigating
              this.navigateToLeg(car);
            } else {
              // Try to move to adjacent paved cell
              this.tryMove(car);
            }
          }
          break;

        case 'driving':
          // Track mission time
          if (car.mission !== 'none') {
            car.missionTimer += dt;
            if (car.missionTimer >= this.patienceFor(car)) {
              this.handleMissionTimeout(car);
              break;
            }
          }

          // Cars move faster on highways
          const moveSpeed = this.getMoveSpeed(car.gridX, car.gridY);
          car.progress += dt / moveSpeed;
          if (car.progress >= 1) {
            // Track position history before updating (for anti-oscillation)
            car.recentPositions.push({ x: car.gridX, y: car.gridY });
            if (car.recentPositions.length > 4) {
              car.recentPositions.shift(); // Keep only last 4 positions
            }

            // Record path for mission route visualization
            if (car.mission !== 'none') {
              car.missionPathHistory.push({ x: car.gridX, y: car.gridY });
            }

            car.gridX = car.targetGridX;
            car.gridY = car.targetGridY;
            car.progress = 0;
            car.cellsRemaining--;

            // Deposit directional pheromone trail
            this.depositPheromone(car.gridX, car.gridY, car.dirX, car.dirY);

            // Check if on a mission and arrived at destination
            if (car.mission !== 'none') {
              this.checkMissionProgress(car);
              if (car.state !== 'driving') break; // Mission changed state
            }

            // Continue in same direction if possible
            if (car.cellsRemaining > 0) {
              const canContinue = this.tryContinueInDirection(car);
              if (!canContinue) {
                // Hit obstacle or edge
                if (car.mission !== 'none') {
                  // On a mission - re-navigate towards destination
                  this.navigateToLeg(car);
                } else if (car.seekingParkingSpot) {
                  // Seeking parking spot - re-navigate
                  this.navigateToParkingSpot(car);
                } else {
                  // No mission - pick new direction
                  this.pickNewDirection(car);
                }
              }
            } else {
              // Finished this run
              if (car.mission !== 'none') {
                // On a mission - keep navigating
                this.navigateToLeg(car);
              } else {
                // No mission - seek valid parking spot
                if (car.seekingParkingSpot && car.parkingTargetX !== null && car.parkingTargetY !== null) {
                  // Already seeking - continue towards parking spot
                  this.navigateToParkingSpot(car);
                } else if (this.isValidParkingSpot(car.gridX, car.gridY)) {
                  // At a valid parking spot - park here
                  car.state = 'parked';
                  car.parkTimer = 0;
                  car.parkDuration = this.rng.range(3000, 8000);
                  car.seekingParkingSpot = false;
                  car.parkingTargetX = null;
                  car.parkingTargetY = null;
                } else {
                  // Find and seek a parking spot
                  const spot = this.findParkingSpot(car.gridX, car.gridY);
                  if (spot) {
                    car.seekingParkingSpot = true;
                    car.parkingTargetX = spot.x;
                    car.parkingTargetY = spot.y;
                    this.navigateToParkingSpot(car);
                  } else {
                    // No parking spots available - just drive around
                    this.pickNewDirection(car);
                  }
                }
              }
            }
          }
          break;

        case 'despawning':
          car.opacity = Math.max(0, 1 - (car.age - car.lifespan) / this.config.FADE_DURATION);
          if (car.opacity <= 0) {
            this.cars.splice(i, 1);
          }
          break;
      }
    }
  }

  /**
   * Try to continue moving in the current direction.
   */
  private tryContinueInDirection(car: Car): boolean {
    const newX = car.gridX + car.dirX;
    const newY = car.gridY + car.dirY;

    // Apply wraparound for the check
    const wrappedX = this.wrapX(newX);
    const wrappedY = this.wrapY(newY);

    if (this.canMoveTo(car, newX, newY)) {
      car.targetGridX = wrappedX;
      car.targetGridY = wrappedY;
      car.progress = 0;
      car.state = 'driving';
      return true;
    }
    return false;
  }

  /**
   * Pick a new direction with anti-oscillation logic.
   * Prefers continuing straight, avoids recently visited cells.
   */
  private pickNewDirection(car: Car): void {
    const directions = [
      { dx: 1, dy: 0 },   // right
      { dx: 0, dy: 1 },   // down
      { dx: -1, dy: 0 },  // left
      { dx: 0, dy: -1 },  // up
    ];

    // Score each direction (lower is better, with randomness for variety)
    type ScoredDir = { dx: number; dy: number; score: number };
    const scoredDirs: ScoredDir[] = [];

    for (const dir of directions) {
      const newX = this.wrapX(car.gridX + dir.dx);
      const newY = this.wrapY(car.gridY + dir.dy);

      if (!this.canMoveTo(car, car.gridX + dir.dx, car.gridY + dir.dy)) {
        continue;
      }

      // Base score: random for variety
      let score = this.rng.next() * 2;

      // Bonus for continuing in same direction (momentum)
      if (car.dirX === dir.dx && car.dirY === dir.dy) {
        score -= 1.5;
      }

      // Penalty for recently visited positions
      for (let i = 0; i < car.recentPositions.length; i++) {
        const pos = car.recentPositions[car.recentPositions.length - 1 - i];
        if (pos.x === newX && pos.y === newY) {
          score += 6 - i; // Higher penalty for more recent visits
          break;
        }
      }

      // Traffic organization on highways
      score += this.getLaneBias(newX, newY, dir.dx, dir.dy);
      score += this.getPheromeBias(newX, newY, dir.dx, dir.dy);

      scoredDirs.push({ ...dir, score });
    }

    if (scoredDirs.length > 0) {
      // Pick the best direction
      scoredDirs.sort((a, b) => a.score - b.score);
      const bestDir = scoredDirs[0];

      car.dirX = bestDir.dx;
      car.dirY = bestDir.dy;
      car.cellsRemaining = 3 + this.rng.int(18); // 3-20 cells
      car.targetGridX = this.wrapX(car.gridX + bestDir.dx);
      car.targetGridY = this.wrapY(car.gridY + bestDir.dy);
      car.progress = 0;
      car.state = 'driving';
      return;
    }

    // Can't move anywhere, park for a bit
    car.state = 'parked';
    car.parkTimer = 0;
    car.parkDuration = this.rng.range(500, 1500);
  }

  /**
   * Check if a car can move to a specific cell.
   */
  private canMoveTo(car: Car, newX: number, newY: number): boolean {
    // Apply wraparound
    const wrappedX = this.wrapX(newX);
    const wrappedY = this.wrapY(newY);

    const cell = this.grid.getCell(wrappedX, wrappedY);
    if (!cell || !this.isPaved(cell.state)) {
      return false;
    }

    // Check if another car is there
    const occupied = this.cars.some(c =>
      c !== car &&
      c.state !== 'despawning' &&
      ((this.wrapX(c.gridX) === wrappedX && this.wrapY(c.gridY) === wrappedY) ||
       (this.wrapX(c.targetGridX) === wrappedX && this.wrapY(c.targetGridY) === wrappedY))
    );

    return !occupied;
  }

  /**
   * Wrap X coordinate for toroidal map.
   */
  private wrapX(x: number): number {
    const cols = this.grid.getCols();
    return ((x % cols) + cols) % cols;
  }

  /**
   * Wrap Y coordinate for toroidal map.
   */
  private wrapY(y: number): number {
    const rows = this.grid.getRows();
    return ((y % rows) + rows) % rows;
  }

  /**
   * Start a car moving after being parked.
   */
  private tryMove(car: Car): void {
    // If at a valid parking spot, stay parked
    if (this.isValidParkingSpot(car.gridX, car.gridY)) {
      car.parkDuration = this.rng.range(2000, 6000); // Park longer at good spots
      return;
    }

    // Seek a valid parking spot
    if (!car.seekingParkingSpot) {
      const spot = this.findParkingSpot(car.gridX, car.gridY);
      if (spot) {
        car.seekingParkingSpot = true;
        car.parkingTargetX = spot.x;
        car.parkingTargetY = spot.y;
      }
    }

    // Navigate towards parking spot if we have one
    if (car.seekingParkingSpot && car.parkingTargetX !== null && car.parkingTargetY !== null) {
      this.navigateToParkingSpot(car);
    } else {
      // Fallback: just pick a random direction
      this.pickNewDirection(car);
    }
  }

  /**
   * Navigate car towards its parking spot target.
   */
  private navigateToParkingSpot(car: Car): void {
    if (car.parkingTargetX === null || car.parkingTargetY === null) {
      car.seekingParkingSpot = false;
      return;
    }

    // Check if arrived at parking spot
    const dist = this.wrappedDistance(car.gridX, car.gridY, car.parkingTargetX, car.parkingTargetY);
    if (dist === 0) {
      // Arrived - park
      car.seekingParkingSpot = false;
      car.parkingTargetX = null;
      car.parkingTargetY = null;
      car.state = 'parked';
      car.parkTimer = 0;
      car.parkDuration = this.rng.range(3000, 8000);
      return;
    }

    // Navigate towards the parking spot using similar logic to navigateToLeg
    const targetX = car.parkingTargetX;
    const targetY = car.parkingTargetY;

    const directions = [
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: -1 },
    ];

    let bestDir: { dx: number; dy: number } | null = null;
    let bestScore = Infinity;

    for (const dir of directions) {
      const newX = this.wrapX(car.gridX + dir.dx);
      const newY = this.wrapY(car.gridY + dir.dy);

      if (!this.canMoveTo(car, car.gridX + dir.dx, car.gridY + dir.dy)) {
        continue;
      }

      // Score based on distance to target
      let score = this.wrappedDistance(newX, newY, targetX, targetY);

      // Penalty for recently visited positions
      for (let i = 0; i < car.recentPositions.length; i++) {
        const pos = car.recentPositions[car.recentPositions.length - 1 - i];
        if (pos.x === newX && pos.y === newY) {
          score += 5 - i;
        }
      }

      // Momentum bonus
      if (car.dirX === dir.dx && car.dirY === dir.dy) {
        score -= 1;
      }

      // Traffic organization on highways
      score += this.getLaneBias(newX, newY, dir.dx, dir.dy);
      score += this.getPheromeBias(newX, newY, dir.dx, dir.dy);

      if (score < bestScore) {
        bestScore = score;
        bestDir = dir;
      }
    }

    if (bestDir) {
      car.dirX = bestDir.dx;
      car.dirY = bestDir.dy;
      car.targetGridX = this.wrapX(car.gridX + bestDir.dx);
      car.targetGridY = this.wrapY(car.gridY + bestDir.dy);
      car.progress = 0;
      car.state = 'driving';
      car.cellsRemaining = 1;
    } else {
      // Can't move - give up on this parking spot
      car.seekingParkingSpot = false;
      car.parkingTargetX = null;
      car.parkingTargetY = null;
    }
  }

  private trySpawnCar(): void {
    // Find all paved cells, separating highways from regular roads
    const highwayCells: { x: number; y: number }[] = [];
    const regularCells: { x: number; y: number }[] = [];
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.grid.getCell(x, y);
        if (cell && this.isPaved(cell.state)) {
          if (this.grid.isHighwayCell(x, y)) {
            highwayCells.push({ x, y });
          } else {
            regularCells.push({ x, y });
          }
        }
      }
    }

    const allPaved = [...highwayCells, ...regularCells];
    if (allPaved.length === 0) return;

    // Find unoccupied spots
    const isSpotAvailable = (spot: { x: number; y: number }) => {
      return !this.cars.some(car =>
        car.state !== 'despawning' &&
        ((car.gridX === spot.x && car.gridY === spot.y) ||
         (car.targetGridX === spot.x && car.targetGridY === spot.y))
      );
    };

    const availableHighways = highwayCells.filter(isSpotAvailable);
    const availableRegular = regularCells.filter(isSpotAvailable);

    if (availableHighways.length === 0 && availableRegular.length === 0) return;

    // 70% chance to spawn on highway if available, otherwise regular
    let spot: { x: number; y: number };
    // Drawn before the branch, so how many values a spawn consumes does not
    // depend on which cells happen to be free.
    const preferHighway = this.rng.chance(0.7);
    if (availableHighways.length > 0 && (availableRegular.length === 0 || preferHighway)) {
      spot = this.rng.pick(availableHighways)!;
    } else {
      spot = this.rng.pick(availableRegular)!;
    }

    // Pick initial random direction
    const directions = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];
    const initialDir = this.rng.pick(directions)!;

    const car: Car = {
      id: this.nextCarId++,
      gridX: spot.x,
      gridY: spot.y,
      targetGridX: spot.x,
      targetGridY: spot.y,
      progress: 0,
      color: this.rng.pick(CarManager.COLORS)!,
      lifespan: this.rng.range(80_000, 230_000), // 80-230 sim-seconds
      age: 0,
      opacity: 0,
      state: 'spawning',
      parkTimer: 0,
      parkDuration: 0,
      dirX: initialDir.dx,
      dirY: initialDir.dy,
      cellsRemaining: 3 + this.rng.int(18), // 3-20 cells
      // Mission tracking
      mission: 'none',
      passengerFamilyId: null,
      missionPickupHouseId: null,
      missionDestinationHouseId: null,
      missionPickupFacilityId: null,
      missionDestinationFacilityId: null,
      missionTimer: 0,
      // Navigation history
      recentPositions: [],
      missionPathHistory: [],
      // Parking seeking
      seekingParkingSpot: false,
      parkingTargetX: null,
      parkingTargetY: null,
      // A car belongs here. See `dispatchTruck` for the ones that do not.
      freight: null,
    };

    this.cars.push(car);
  }

  private isPaved(state: CellState): boolean {
    return (
      state === CellState.POURED ||
      state === CellState.CURED ||
      state === CellState.PAINTED
    );
  }

  /**
   * Get all cars with their depth values for unified depth sorting.
   * Depth is the interpolated Y position.
   */
  getCarsWithDepth(): { car: Car; depth: number }[] {
    return this.cars.map(car => {
      const depth = car.gridY + (car.targetGridY - car.gridY) * car.progress;
      return { car, depth };
    });
  }

  /**
   * Order-stable view of the traffic, for hashing.
   *
   * Cars keep array order — it is spawn order, and it decides who yields to whom
   * in `isCarOccupied`, so it is a simulation fact rather than an accident.
   * Colour and opacity are excluded: they are how a car looks, not what it does.
   */
  /**
   * Every car's complete state, and the trails.
   *
   * Cars keep array order, which is spawn order and is the order they are
   * updated in. Colour and opacity are included here though they are excluded
   * from the hash: what a car looks like is not a decision, but a reload that
   * repainted every car on the street would still be a reload somebody noticed.
   */
  save(): CarSnapshot {
    return {
      cars: this.cars.map(c => ({
        ...c,
        recentPositions: c.recentPositions.map(p => ({ ...p })),
        missionPathHistory: c.missionPathHistory.map(p => ({ ...p })),
        freight: c.freight ? { ...c.freight } : null,
      })),
      nextCarId: this.nextCarId,
      spawnTimer: this.spawnTimer,
      requestCheckTimer: this.requestCheckTimer,
      // In the order cars laid them down, not sorted — see `core/snapshot.ts`.
      trails: Array.from(this.pheromones.entries())
        .map(([cell, p]) => ({ cell, p: [p[0], p[1], p[2], p[3]] as [number, number, number, number] })),
      rng: this.rng.save(),
    };
  }

  /** Restore from `save()`. */
  load(state: CarSnapshot): void {
    this.cars = state.cars.map(c => ({
      ...c,
      recentPositions: c.recentPositions.map(p => ({ ...p })),
      missionPathHistory: c.missionPathHistory.map(p => ({ ...p })),
      freight: c.freight ? { ...c.freight } : null,
    }));
    // A truck mid-crossing is restored mid-crossing, and its outcome will be
    // reported by whichever run finishes it. The tray is drained every tick, so
    // there has never been an outcome in flight at a snapshot boundary.
    this.freightOutcomes = [];
    this.nextCarId = state.nextCarId;
    this.spawnTimer = state.spawnTimer;
    this.requestCheckTimer = state.requestCheckTimer;
    this.pheromones.clear();
    for (const trail of state.trails) {
      this.pheromones.set(trail.cell, [trail.p[0], trail.p[1], trail.p[2], trail.p[3]]);
    }
    this.bfsCache.clear();
    this.rng.load(state.rng);
  }

  /**
   * Traffic pheromone sorted by cell: the Map is keyed by whichever cell a car
   * drove over first, which is history rather than state.
   */
  private sortedTrails(): Array<{ cell: string; p: [number, number, number, number] }> {
    return Array.from(this.pheromones.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
      .map(([cell, p]) => ({ cell, p: [p[0], p[1], p[2], p[3]] as [number, number, number, number] }));
  }

  canonicalState(): Canonical {
    const cars = this.cars.map(c => {
      const record: Record<string, Canonical> = {
        id: c.id,
        x: c.gridX,
        y: c.gridY,
        tx: c.targetGridX,
        ty: c.targetGridY,
        progress: c.progress,
        state: c.state,
        age: c.age,
        lifespan: c.lifespan,
        parkTimer: c.parkTimer,
        parkDuration: c.parkDuration,
        dirX: c.dirX,
        dirY: c.dirY,
        cellsRemaining: c.cellsRemaining,
        mission: c.mission,
        passenger: c.passengerFamilyId,
        pickup: c.missionPickupHouseId,
        destination: c.missionDestinationHouseId,
        missionTimer: c.missionTimer,
        recent: c.recentPositions.map(p => `${p.x},${p.y}`),
        seeking: c.seekingParkingSpot,
        parkTargetX: c.parkingTargetX,
        parkTargetY: c.parkingTargetY,
      };
      // Written only for a leg that names a gate. A car carrying somebody to the
      // shops hashes exactly as it did before commuting existed, which is why no
      // golden master moved for ADR-016.
      if (c.missionPickupFacilityId !== null) {
        record.pickupFacility = c.missionPickupFacilityId;
      }
      if (c.missionDestinationFacilityId !== null) {
        record.destinationFacility = c.missionDestinationFacilityId;
      }
      // And on the same terms for freight: a district with no corridor hashes to
      // what it hashed to before there were trucks. Two keys rather than a nested
      // record, so a leg reads as a leg in a diff of two hashes.
      if (c.freight) {
        record.leg = c.freight.legId;
        record.legTo = `${c.freight.toX},${c.freight.toY}`;
        // And only for a lorry with a delivery address: a transit crossing hashes
        // to what it hashed to before there were building sites.
        if (c.freight.facilityId !== null) record.legFor = c.freight.facilityId;
      }
      return record;
    });

    const trails = this.sortedTrails();

    return {
      cars,
      nextCarId: this.nextCarId,
      spawnTimer: this.spawnTimer,
      requestCheckTimer: this.requestCheckTimer,
      trails,
      rng: this.rng.save() as unknown as Canonical,
    };
  }

  /**
   * The district's own cars.
   *
   * Freight is excluded, and the Chronicle's `town.cars` is this figure: how many
   * vehicles a town has is a fact about the town, and a truck crossing it on
   * somebody else's business is not one. What the trucks are is
   * `freight.legsDispatched`, and the two are deliberately different lines.
   */
  getCarCount(): number {
    return this.cars.filter(c => c.state !== 'despawning' && c.freight === null).length;
  }

  /** Trucks on the map right now. Zero in a district off the corridor. */
  getTruckCount(): number {
    return this.cars.filter(c => c.state !== 'despawning' && c.freight !== null).length;
  }

  getParkedCount(): number {
    return this.cars.filter(c => c.state === 'parked').length;
  }

  /**
   * Get all cars for collision detection.
   * Parked cars don't kill ants, so they are excluded from collision checks.
   *
   * Trucks are in here, and nothing was written to put them there. §5 lists *"ant
   * deaths from trucks it never ordered"* under *"consequences that fall out for
   * free"*, and this is the line where it falls out: a truck is a vehicle in this
   * array, and what a vehicle does to what is under it has been the same predicate
   * since long before there was a polity.
   */
  getCars(): readonly {
    gridX: number;
    gridY: number;
    targetGridX: number;
    targetGridY: number;
    progress: number;
    state: string;
  }[] {
    return this.cars;
  }

  /**
   * Check if a car at a position is parked (for collision exemption).
   */
  isCarParkedAt(gridX: number, gridY: number): boolean {
    for (const car of this.cars) {
      if (car.state !== 'parked') continue;
      if (car.gridX === gridX && car.gridY === gridY) {
        return true;
      }
    }
    return false;
  }

  /**
   * Find a valid parking spot: paved cell with at least 2 connections to non-paved cells.
   * This puts cars at the edge of parking areas, leaving the road clear.
   */
  findParkingSpot(fromX: number, fromY: number): { x: number; y: number } | null {
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();
    const candidates: { x: number; y: number; distance: number }[] = [];

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.grid.getCell(x, y);
        if (!cell || !this.isPaved(cell.state)) continue;

        // Count non-paved neighbors (including diagonals for "connection")
        let nonPavedCount = 0;
        const neighbors = [
          { dx: 1, dy: 0 },
          { dx: -1, dy: 0 },
          { dx: 0, dy: 1 },
          { dx: 0, dy: -1 },
        ];

        for (const n of neighbors) {
          const nx = x + n.dx;
          const ny = y + n.dy;
          const neighbor = this.grid.getCell(nx, ny);
          // Out of bounds or non-paved counts as non-route
          if (!neighbor || !this.isPaved(neighbor.state)) {
            nonPavedCount++;
          }
        }

        // Need at least 2 connections to non-paved cells
        if (nonPavedCount >= 2) {
          // Check if spot is not occupied by another car
          const occupied = this.cars.some(c =>
            c.state !== 'despawning' &&
            ((c.gridX === x && c.gridY === y) ||
             (c.targetGridX === x && c.targetGridY === y))
          );

          if (!occupied) {
            const dist = this.wrappedDistance(fromX, fromY, x, y);
            candidates.push({ x, y, distance: dist });
          }
        }
      }
    }

    if (candidates.length === 0) return null;

    // Sort by distance and return nearest
    candidates.sort((a, b) => a.distance - b.distance);
    return { x: candidates[0].x, y: candidates[0].y };
  }

  /**
   * Check if a position is a valid parking spot (has >= 2 non-paved neighbors).
   */
  isValidParkingSpot(x: number, y: number): boolean {
    const cell = this.grid.getCell(x, y);
    if (!cell || !this.isPaved(cell.state)) return false;

    let nonPavedCount = 0;
    const neighbors = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    for (const n of neighbors) {
      const nx = x + n.dx;
      const ny = y + n.dy;
      const neighbor = this.grid.getCell(nx, ny);
      if (!neighbor || !this.isPaved(neighbor.state)) {
        nonPavedCount++;
      }
    }

    return nonPavedCount >= 2;
  }

  /**
   * Get car at a specific grid position (for hover info).
   */
  getCarAt(gridX: number, gridY: number): CarInfo | null {
    for (const car of this.cars) {
      if (car.state === 'despawning') continue;

      // Check current interpolated position
      const carX = Math.round(car.gridX + (car.targetGridX - car.gridX) * car.progress);
      const carY = Math.round(car.gridY + (car.targetGridY - car.gridY) * car.progress);

      if (carX === gridX && carY === gridY) {
        return describeCar(car);
      }
    }
    return null;
  }

  /**
   * Get car ID at a specific grid position.
   */
  getCarIdAt(gridX: number, gridY: number): number | null {
    for (const car of this.cars) {
      if (car.state === 'despawning') continue;

      const carX = Math.round(car.gridX + (car.targetGridX - car.gridX) * car.progress);
      const carY = Math.round(car.gridY + (car.targetGridY - car.gridY) * car.progress);

      if (carX === gridX && carY === gridY) {
        return car.id;
      }
    }
    return null;
  }

  /**
   * Get car position by ID.
   */
  getCarPositionById(carId: number): { gridX: number; gridY: number } | null {
    const car = this.cars.find(c => c.id === carId);
    if (!car || car.state === 'despawning') return null;

    return {
      gridX: Math.round(car.gridX + (car.targetGridX - car.gridX) * car.progress),
      gridY: Math.round(car.gridY + (car.targetGridY - car.gridY) * car.progress),
    };
  }

  /**
   * Get full car info by ID (for tracked car tooltip).
   */
  getCarInfoById(carId: number): (CarInfo & { gridX: number; gridY: number }) | null {
    const car = this.cars.find(c => c.id === carId);
    if (!car || car.state === 'despawning') return null;

    return {
      ...describeCar(car),
      gridX: Math.round(car.gridX + (car.targetGridX - car.gridX) * car.progress),
      gridY: Math.round(car.gridY + (car.targetGridY - car.gridY) * car.progress),
    };
  }

  /**
   * Get the full trip path for a car: traveled history + predicted remaining path.
   * Returns null if the car has no active mission.
   */
  getCarTripPath(carId: number): { traveled: { x: number; y: number }[]; remaining: { x: number; y: number }[] } | null {
    const car = this.cars.find(c => c.id === carId);
    if (!car || car.mission === 'none') return null;

    const target = this.legTarget(car);
    if (!target) {
      return { traveled: [...car.missionPathHistory], remaining: [] };
    }

    const remaining = this.traceRemainingPath(car.gridX, car.gridY, target.x, target.y);

    return {
      traveled: [...car.missionPathHistory],
      remaining,
    };
  }

  /**
   * Trace the predicted remaining path from a position to a destination using BFS gradient descent.
   */
  private traceRemainingPath(fromX: number, fromY: number, houseX: number, houseY: number): { x: number; y: number }[] {
    const bfsMap = this.getBFSDistanceMap(houseX, houseY);
    const path: { x: number; y: number }[] = [{ x: fromX, y: fromY }];
    const visited = new Set<string>();
    visited.add(`${fromX},${fromY}`);

    const directions = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    let cx = fromX;
    let cy = fromY;

    for (let step = 0; step < 200; step++) {
      const curDist = bfsMap.get(`${cx},${cy}`);
      if (curDist !== undefined && curDist <= 0) break; // Arrived

      let bestX = -1;
      let bestY = -1;
      let bestDist = Infinity;

      for (const dir of directions) {
        const nx = this.wrapX(cx + dir.dx);
        const ny = this.wrapY(cy + dir.dy);
        const key = `${nx},${ny}`;
        const dist = bfsMap.get(key);
        if (dist !== undefined && dist < bestDist && !visited.has(key)) {
          bestDist = dist;
          bestX = nx;
          bestY = ny;
        }
      }

      if (bestX === -1) break; // No reachable neighbor

      visited.add(`${bestX},${bestY}`);
      path.push({ x: bestX, y: bestY });
      cx = bestX;
      cy = bestY;
    }

    return path;
  }
}
